/**
 * Delete (soft-delete) a campaign. Migration 203: admins/superadmins only, unsent campaigns only.
 * Hidden for everyone else; shown disabled with an explanation when a flight was scheduled or sent.
 * The database is the authority — a refusal is shown in plain language.
 */
import React, { useState } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";
import { softDeleteCampaign } from "../../services/campaigns.service";
import {
  campaignDeleteButtonState,
  campaignDeleteErrorMessage,
  CAMPAIGN_DELETE_CONFIRM,
  CAMPAIGN_DELETE_SENT_TOOLTIP,
} from "../../utils/campaignSoftDelete";

interface Props {
  campaign: { id: string; name: string; is_deleted?: boolean | null };
  flightStatuses: ReadonlyArray<string | null | undefined>;
  onDeleted: () => void;
  /** "icon" for table rows, "button" for the campaign page header. */
  variant?: "icon" | "button";
}

const DeleteCampaignButton: React.FC<Props> = ({
  campaign,
  flightStatuses,
  onDeleted,
  variant = "icon",
}) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const state = campaignDeleteButtonState(user?.role, campaign, flightStatuses);
  if (state === "hidden") {
    return null;
  }
  const disabled = state === "disabled_sent" || busy;
  const title =
    state === "disabled_sent"
      ? CAMPAIGN_DELETE_SENT_TOOLTIP
      : "Delete campaign";

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || !window.confirm(CAMPAIGN_DELETE_CONFIRM(campaign.name))) {
      return;
    }
    setBusy(true);
    try {
      await softDeleteCampaign(campaign.id);
      onDeleted();
    } catch (err) {
      alert(campaignDeleteErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (variant === "button") {
    return (
      <span
        title={title}
        data-testid="delete-campaign-button-wrap"
        className="inline-flex"
      >
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          aria-label="Delete campaign"
          data-testid="delete-campaign-button"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-red-200 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          <TrashIcon className="h-4 w-4" />
          {busy ? "Deleting…" : "Delete"}
        </button>
      </span>
    );
  }

  return (
    <span title={title} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-label="Delete campaign"
        data-testid="delete-campaign-button"
        className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-400"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </span>
  );
};

export default DeleteCampaignButton;
